import { NextPage } from 'next';
import {
  useMediaQuery,
  Box,
  Stack,
  Heading,
  Icon,
  Grid,
  Center,
  GridItem
} from '@chakra-ui/react';
import {
  Card,
  FAQs,
  Hero,
  PlansTable,
  PlansTableMobile
} from '@components/pricing';
import {
  LayoutContainer,
  NewsLetter,
  PartnerLogos,
  RoundedCorner
} from '@components/common';
import { singlePageProps } from '@lib';
import { colors } from '@theme/foundations';
import { useEffect, useState } from 'react';
import { analytics, rudderTrack } from 'pages';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { rudderPage } from './_app';

const planInfo = [
  {
    badgeText: 'Free',
    price: '$0',
    description: `Unlimited sign-ins, no credit card required. Limit 5,000 monthly active users.`,
    features: [
      'Verification emails',
      'Custom domains',
      'Pre-built components',
      'Clerk branding',
      'Community support'
    ],
    featuresTitle: 'Included features'
  },
  {
    badgeText: 'Hobby',
    price: '$25',
    description: `Essential features for launching and growing any application.`,
    features: [
      'Unlimited MAUs',
      'Allowlist/Blocklist',
      'Additional MAUs/MAOs',
      'Remove Clerk Branding',
      'Verification SMSs'
    ],
    featuresTitle: 'Everything in free, plus'
  },
  {
    badgeText: 'Business',
    price: '$99',
    description: `Everything you need for building a business.`,
    features: [
      'Custom session duration',
      'Multifactor authentication',
      'User impersonation',
      'Device management',
      'Priority support & SOC2 Report'
    ],
    featuresTitle: 'Everything in hobby, plus'
  },
  {
    badgeText: 'Enterprise',
    price: 'Contact Us',
    description: `Bulk discounts, custom contracts and enterprise support.`,
    features: [
      'Bulk discounts',
      '99.99% Uptime SLA',
      'Custom feature dev',
      'HIPAA BAA',
      'Premium support, 24/7/365'
    ],
    featuresTitle: 'Everything in business, plus'
  }
];

const Pricing: NextPage = () => {
  const [isSmallerThan1000] = useMediaQuery('(max-width: 1000px)');
  const [isLargerThan1400] = useMediaQuery('(min-width: 1400px)');

  useEffect(() => {
    analytics.page('Marketing_Pricing_Page Viewed');
    rudderPage('Marketing_Pricing_Page Viewed');
  }, []);

  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    setShouldRender(true);
  }, []);

  // Workaround to prevent `Hydration failed because the initial UI does not match what was rendered on the server.`
  if (!shouldRender) return <Box h="100vh" w="100vw"></Box>;
  return (
    <>
      <LayoutContainer
        pos="relative"
        verticalPadding="none"
        noHorizontalPadding
        bgGradient="linear(to-br, #E5F8FF 0%, white 40%, #E5F8FF 100%)"
        pt="80px"
        zIndex={1}
      >
        <Hero />
        <Center>
          <Grid
            templateColumns={
              !isLargerThan1400
                ? { base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }
                : 'repeat(4, 1fr)'
            }
            gap={'44px'}
            justifyContent="space-evenly"
          >
            {planInfo.map((plan, index) => (
              <GridItem key={index}>
                <Card
                  badgeText={plan.badgeText}
                  price={plan.price}
                  description={plan.description}
                  featuresTitle={plan.featuresTitle}
                  features={plan.features}
                />
              </GridItem>
            ))}
          </Grid>
        </Center>
        <PartnerLogos />
        <RoundedCorner
          xPosition="left"
          yPosition="bottom"
          bg="#FAFDFF"
          canvasHeight={160}
        />
        <RoundedCorner
          xPosition="right"
          yPosition="bottom"
          bg="#E5F8FF"
          canvasHeight={160}
        />
      </LayoutContainer>
      <Stack spacing={{ base: '80px' }} bg="white">
        <Heading
          alignSelf={'center'}
          pos="relative"
          display={'flex'}
          flexDir="column"
          justifyContent={'center'}
          alignItems="center"
          fontSize={{ base: '40px', lg: '56px' }}
          mt="80px"
        >
          Plan Features
          <AnimatePresence>
            <motion.div
              transition={{ ease: 'linear', duration: 2, repeat: Infinity }}
              animate={{ y: [null, 10, 20, 10, 0] }}
            >
              <Icon
                as={ChevronDownIcon}
                h="38px"
                w="38px"
                color="primary.500"
              ></Icon>
            </motion.div>
          </AnimatePresence>
        </Heading>
        {isSmallerThan1000 ? <PlansTableMobile /> : <PlansTable />}
        <Box bg="white" py={{ base: 20, lg: 0 }}>
          <FAQs />
          <NewsLetter />
        </Box>
      </Stack>
    </>
  );
};

export default Pricing;

export async function getStaticProps({ preview = false }) {
  return singlePageProps({ preview, slug: '/pricing' });
}
